import { CheckCircleIcon } from '@heroicons/react/24/solid';
import cx from 'classnames';

interface RewardProps {
  name: string;
  description: string;
  disclaimer?: string;
  image: string;
  isAchieved: boolean;
  numerator: number;
  denominator: number;
  translations: Record<string, string>;
}

const Reward = ({
  name,
  description,
  disclaimer,
  image,
  isAchieved,
  numerator,
  denominator,
  translations,
}: RewardProps) => {
  return (
    <div className="relative w-full">
      {isAchieved && <CheckCircleIcon className="absolute -right-2 -top-2 z-10 h-6 w-6 text-green-500" />}
      <div className={cx('relative space-y-2 rounded-md border border-gray-200 p-4', isAchieved ? 'opacity-50' : '')}>
        <div className="flex justify-between">
          <div className="flex w-fit flex-col space-x-0 sm:w-full sm:flex-row sm:space-x-4">
            <div className="mb-4 h-[132px] w-[132px] rounded-md border border-[#E5E7EB] overflow-hidden grow-1 shrink-0">
              <img src={image} alt="Reward" className="object-cover w-full h-full aspect-square" />
            </div>
            <div className="w-full">
              <h4 className="pb-1 text-lg text-gray-900">{name}</h4>
              <p className="font-regular text-gray-500 text-sm">{description}</p>
              {disclaimer && (
                <div className="mt-2 flex gap-x-1 items-center">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">{`${translations?.['referrals.reward.disclaimer']}:`}</span>{' '}
                    {disclaimer}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:gap-5 sm:flex-row sm:flex-row-reverse sm:justify-between">
          <h4 className="tracking-medium mb-1 h-fit text-lg font-bold leading-tight text-gray-900 flex-shrink-0 w-auto text-right">
            {numerator}/{denominator}
          </h4>

          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <div className="relative overflow-hidden h-1 w-full bg-[#F3F4F6] rounded-full">
              <div
                className="h-2 w-full"
                style={{
                  backgroundColor: 'var(--pt-background-color)',
                  width: `${Math.min(Math.round((numerator / denominator) * 100), 100)}%`,
                }}
              />
            </div>

            <p className="text-gray-900 font-light text-xs leading-4">
              {translations?.['referrals.reward.progress.description']}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reward;
